import { useState, useEffect, useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '../components/layout/TopBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Plus, Star, Calendar, Clock, Users, DollarSign, Image as ImageIcon,
  Eye, Edit2, Trash2, RefreshCw, Search, Settings, Loader2, Check,
  X, Heart, Sparkles, Coffee, Music, Gift, Wine, Camera, BookOpen,
  TrendingUp, BarChart3, Archive, PlayCircle, PauseCircle, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Status config ─────────────────────────────────────────────────────────
const EXP_STATUS = {
  draft:    { label: 'Borrador',  pill: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
  active:   { label: 'Activa',   pill: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  paused:   { label: 'Pausada',  pill: 'bg-amber-100 text-amber-700',    dot: 'bg-amber-500' },
  archived: { label: 'Archivada',pill: 'bg-slate-100 text-slate-500',    dot: 'bg-slate-300' },
};

const CATEGORY_ICONS = {
  dining:    Coffee,
  wine:      Wine,
  music:     Music,
  photo:     Camera,
  art:       Star,
  cooking:   Coffee,
  romantic:  Heart,
  special:   Gift,
  other:     Sparkles,
};

const CATEGORY_LABELS = {
  dining:    'Cena especial',
  wine:      'Cata de vinos',
  music:     'Música en vivo',
  photo:     'Sesión fotográfica',
  art:       'Arte & Cultura',
  cooking:   'Clase de cocina',
  romantic:  'Experiencia romántica',
  special:   'Ocasión especial',
  other:     'Otro',
};

const DEFAULT_PRICING = [
  { type: 'individual', label: 'Individual', price: 0, max_guests: 1,  description: 'Para una persona' },
  { type: 'couple',     label: 'Pareja',     price: 0, max_guests: 2,  description: 'Para dos personas' },
  { type: 'group',      label: 'Grupo',      price: 0, max_guests: 10, description: 'Hasta 10 personas' },
];

// ─── Experience Card ─────────────────────────────────────────────────────────
function ExperienceCard({ exp, onEdit, onDelete, onStatusChange, onView }) {
  const st = EXP_STATUS[exp.status] || EXP_STATUS.draft;
  const Icon = CATEGORY_ICONS[exp.category] || Sparkles;
  const minPrice = exp.pricing_options?.length
    ? Math.min(...exp.pricing_options.map(p => p.price || 0))
    : 0;
  const bookings = exp.total_bookings || 0;
  const revenue = exp.total_revenue || 0;

  return (
    <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col group">
      {/* Image / Hero */}
      <div className="relative h-44 bg-gradient-to-br from-orange-400 to-pink-600 overflow-hidden flex-shrink-0">
        {exp.image_url ? (
          <img src={exp.image_url} alt={exp.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            <Icon className="h-10 w-10 text-white/60" />
            <span className="text-white/40 text-xs font-medium">{CATEGORY_LABELS[exp.category] || 'Experiencia'}</span>
          </div>
        )}
        {/* Status badge */}
        <div className="absolute top-3 left-3">
          <span className={cn("text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm", st.pill)}>
            {st.label}
          </span>
        </div>
        {/* Quick actions overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={() => onView(exp)}>
            <Eye className="h-3.5 w-3.5 mr-1" /> Ver
          </Button>
          <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={() => onEdit(exp)}>
            <Edit2 className="h-3.5 w-3.5 mr-1" /> Editar
          </Button>
        </div>
      </div>

      <CardContent className="p-4 flex flex-col flex-1">
        <div className="flex-1">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-bold text-sm leading-tight line-clamp-2 flex-1">{exp.name}</h3>
          </div>
          {exp.description && (
            <p className="text-xs text-slate-500 line-clamp-2 mb-3">{exp.description}</p>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 mb-3">
            {exp.duration_minutes && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-blue-400" /> {exp.duration_minutes}min
              </span>
            )}
            {exp.max_guests && (
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3 text-purple-400" /> máx {exp.max_guests}
              </span>
            )}
            {minPrice > 0 && (
              <span className="flex items-center gap-1 font-semibold text-emerald-600">
                <DollarSign className="h-3 w-3" /> desde ${minPrice.toLocaleString()}
              </span>
            )}
          </div>
          {(bookings > 0 || revenue > 0) && (
            <div className="flex gap-3 text-xs border-t pt-2 mb-2">
              <span className="text-slate-500">{bookings} reservas</span>
              {revenue > 0 && (
                <span className="font-semibold text-emerald-600">${revenue.toLocaleString()}</span>
              )}
            </div>
          )}
        </div>

        {/* Status toggle */}
        <div className="flex gap-1 mt-2">
          {exp.status !== 'active' && (
            <Button size="sm" variant="outline" className="flex-1 h-7 text-[11px] text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              onClick={() => onStatusChange(exp.id, 'active')}>
              <PlayCircle className="h-3 w-3 mr-1" /> Activar
            </Button>
          )}
          {exp.status === 'active' && (
            <Button size="sm" variant="outline" className="flex-1 h-7 text-[11px] text-amber-600 border-amber-200 hover:bg-amber-50"
              onClick={() => onStatusChange(exp.id, 'paused')}>
              <PauseCircle className="h-3 w-3 mr-1" /> Pausar
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 text-red-400 hover:text-red-600"
            onClick={() => onDelete(exp.id)}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Experience Form ─────────────────────────────────────────────────────────
function ExperienceForm({ experience, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: '', description: '', category: 'dining',
    duration_minutes: 120, max_guests: 10,
    image_url: '', video_url: '',
    pricing_options: DEFAULT_PRICING.map(p => ({ ...p })),
    includes: '', requirements: '',
    is_available: true,
    available_days: ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'],
    ...experience,
    pricing_options: experience?.pricing_options?.length
      ? experience.pricing_options
      : DEFAULT_PRICING.map(p => ({ ...p })),
  });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('basic');

  const setPricing = (i, field, value) => {
    setForm(f => {
      const opts = [...f.pricing_options];
      opts[i] = { ...opts[i], [field]: field === 'price' || field === 'max_guests' ? +value || 0 : value };
      return { ...f, pricing_options: opts };
    });
  };
  const addPricing = () => setForm(f => ({
    ...f,
    pricing_options: [...f.pricing_options, { type: 'custom', label: 'Personalizado', price: 0, max_guests: 4, description: '' }]
  }));
  const removePricing = (i) => setForm(f => ({ ...f, pricing_options: f.pricing_options.filter((_, idx) => idx !== i) }));

  const DAYS = [
    { k: 'monday', l: 'Lun' }, { k: 'tuesday', l: 'Mar' }, { k: 'wednesday', l: 'Mié' },
    { k: 'thursday', l: 'Jue' }, { k: 'friday', l: 'Vie' }, { k: 'saturday', l: 'Sáb' }, { k: 'sunday', l: 'Dom' },
  ];

  const toggleDay = (d) => {
    setForm(f => ({
      ...f,
      available_days: f.available_days?.includes(d)
        ? f.available_days.filter(x => x !== d)
        : [...(f.available_days || []), d]
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      const endpoint = experience?.id ? `/api/experiences/${experience.id}` : '/api/experiences';
      const method = experience?.id ? 'put' : 'post';
      const res = await (method === 'put'
        ? fetch(`${API_URL}/${endpoint.slice(1)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify(payload),
          })
        : fetch(`${API_URL}/api/experiences`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify(payload),
          })
      );
      if (!res.ok) throw new Error('Error');
      toast.success(experience?.id ? 'Experiencia actualizada' : 'Experiencia creada');
      onSave();
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full">
          <TabsTrigger value="basic" className="flex-1 text-xs">General</TabsTrigger>
          <TabsTrigger value="pricing" className="flex-1 text-xs">Precios</TabsTrigger>
          <TabsTrigger value="availability" className="flex-1 text-xs">Disponibilidad</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-3 pt-3">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Nombre *</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Cena romántica bajo las estrellas..." className="text-sm" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Descripción</Label>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe la experiencia que vivirán tus clientes..." rows={3} className="text-sm resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Categoría</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Duración (min)</Label>
              <Input type="number" min={30} step={30} value={form.duration_minutes}
                onChange={e => setForm(f => ({ ...f, duration_minutes: +e.target.value || 60 }))} className="text-sm h-9" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Capacidad máxima</Label>
            <Input type="number" min={1} value={form.max_guests}
              onChange={e => setForm(f => ({ ...f, max_guests: +e.target.value || 1 }))} className="text-sm h-9" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">URL imagen</Label>
            <Input value={form.image_url || ''} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
              placeholder="https://..." className="text-sm h-9" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Incluye</Label>
            <Textarea value={form.includes || ''} onChange={e => setForm(f => ({ ...f, includes: e.target.value }))}
              placeholder="Mesa decorada, botella de vino, postre especial..." rows={2} className="text-sm resize-none" />
          </div>
        </TabsContent>

        <TabsContent value="pricing" className="space-y-3 pt-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-700">Opciones de precio</p>
            <Button size="sm" variant="outline" onClick={addPricing} className="h-7 text-xs">
              <Plus className="h-3.5 w-3.5 mr-1" /> Agregar
            </Button>
          </div>
          {form.pricing_options.map((opt, i) => (
            <div key={i} className="border rounded-xl p-3 space-y-2 bg-slate-50">
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <Label className="text-[10px] text-slate-400 mb-0.5 block">Nombre del plan</Label>
                  <Input value={opt.label} onChange={e => setPricing(i, 'label', e.target.value)}
                    placeholder="Individual, Pareja..." className="text-xs h-8" />
                </div>
                <Button size="sm" variant="ghost" onClick={() => removePricing(i)}
                  className="h-8 w-8 p-0 text-red-400 hover:text-red-600 mt-4">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-400 mb-0.5 block">Precio (MXN)</Label>
                  <Input type="number" min={0} value={opt.price} onChange={e => setPricing(i, 'price', e.target.value)}
                    className="text-xs h-8" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-400 mb-0.5 block">Máx. personas</Label>
                  <Input type="number" min={1} value={opt.max_guests} onChange={e => setPricing(i, 'max_guests', e.target.value)}
                    className="text-xs h-8" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-slate-400 mb-0.5 block">Descripción</Label>
                <Input value={opt.description || ''} onChange={e => setPricing(i, 'description', e.target.value)}
                  placeholder="Descripción breve..." className="text-xs h-8" />
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="availability" className="space-y-3 pt-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-slate-600 font-medium">Disponible</Label>
            <Switch checked={form.is_available} onCheckedChange={v => setForm(f => ({ ...f, is_available: v }))} />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-2 block">Días disponibles</Label>
            <div className="flex gap-1.5 flex-wrap">
              {DAYS.map(d => (
                <button key={d.k} onClick={() => toggleDay(d.k)}
                  className={cn("px-3 py-1.5 rounded-lg text-xs font-medium border transition-all",
                    form.available_days?.includes(d.k)
                      ? "border-orange-500 bg-orange-50 text-orange-700"
                      : "border-slate-200 text-slate-500 hover:border-slate-300")}>
                  {d.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Requisitos previos</Label>
            <Textarea value={form.requirements || ''} onChange={e => setForm(f => ({ ...f, requirements: e.target.value }))}
              placeholder="Reserva mínima 48h de anticipación..." rows={2} className="text-sm resize-none" />
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex gap-2 pt-2 border-t mt-2">
        <Button variant="outline" className="flex-1" onClick={onCancel}>Cancelar</Button>
        <Button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
          {experience?.id ? 'Guardar cambios' : 'Crear experiencia'}
        </Button>
      </div>
    </div>
  );
}

// ─── Experience Detail Drawer ─────────────────────────────────────────────────
function ExperienceDetail({ exp, onClose }) {
  const navigate = useNavigate();
  const Icon = CATEGORY_ICONS[exp.category] || Sparkles;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Hero */}
        <div className="relative h-52 bg-gradient-to-br from-orange-400 to-pink-600 flex-shrink-0">
          {exp.image_url
            ? <img src={exp.image_url} alt={exp.name} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center"><Icon className="h-16 w-16 text-white/40" /></div>
          }
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <button onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white">
            <X className="h-4 w-4" />
          </button>
          <div className="absolute bottom-4 left-4 text-white">
            <div className="text-[11px] opacity-70 mb-0.5">{CATEGORY_LABELS[exp.category]}</div>
            <h2 className="text-lg font-bold leading-tight">{exp.name}</h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { l: 'Duración', v: exp.duration_minutes ? `${exp.duration_minutes}min` : '—', icon: Clock },
              { l: 'Capacidad', v: exp.max_guests ? `${exp.max_guests}p` : '—', icon: Users },
              { l: 'Reservas',  v: exp.total_bookings || 0,  icon: Calendar },
            ].map(s => (
              <div key={s.l} className="rounded-xl border p-3 text-center">
                <s.icon className="h-4 w-4 mx-auto mb-1 text-orange-400" />
                <div className="text-base font-bold">{s.v}</div>
                <div className="text-[10px] text-slate-500">{s.l}</div>
              </div>
            ))}
          </div>

          {/* Description */}
          {exp.description && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-1">Descripción</h4>
              <p className="text-sm text-slate-600 leading-relaxed">{exp.description}</p>
            </div>
          )}

          {/* Pricing */}
          {exp.pricing_options?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Opciones de precio</h4>
              <div className="space-y-2">
                {exp.pricing_options.map((opt, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div>
                      <p className="text-sm font-medium">{opt.label}</p>
                      <p className="text-xs text-slate-500">
                        {opt.description || `Hasta ${opt.max_guests} personas`}
                      </p>
                    </div>
                    <span className="font-bold text-emerald-600">
                      {opt.price > 0 ? `$${opt.price.toLocaleString()}` : 'Gratis'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Includes */}
          {exp.includes && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-1">Incluye</h4>
              <p className="text-sm text-slate-600">{exp.includes}</p>
            </div>
          )}

          {/* Available days */}
          {exp.available_days?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Disponibilidad</h4>
              <div className="flex gap-1.5 flex-wrap">
                {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map((d, i) => {
                  const keys = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
                  const active = exp.available_days.includes(keys[i]);
                  return (
                    <span key={d} className={cn("px-3 py-1 rounded-lg text-xs font-medium",
                      active ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-400 line-through")}>{d}</span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Revenue */}
          {(exp.total_revenue || 0) > 0 && (
            <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                <span className="text-sm text-emerald-700">Ingresos totales:</span>
                <span className="font-bold text-emerald-700">${exp.total_revenue.toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t">
          <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            onClick={() => navigate('/experience-widget')}>
            <Settings className="h-4 w-4 mr-2" /> Configurar widget
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Experiences() {
  const navigate = useNavigate();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingExp, setEditingExp] = useState(null);
  const [viewingExp, setViewingExp] = useState(null);

  const fetchExperiences = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/experiences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error');
      const data = await res.json();
      setExperiences(Array.isArray(data) ? data : []);
    } catch { toast.error('Error al cargar experiencias'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchExperiences(); }, [fetchExperiences]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta experiencia?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/experiences/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      setExperiences(prev => prev.filter(e => e.id !== id));
      toast.success('Experiencia eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/experiences/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      setExperiences(prev => prev.map(e => e.id === id ? { ...e, status } : e));
      toast.success(`Experiencia ${EXP_STATUS[status]?.label?.toLowerCase()}`);
    } catch { toast.error('Error al actualizar'); }
  };

  const filtered = useMemo(() => {
    return experiences.filter(e => {
      const q = searchTerm.toLowerCase();
      const matchSearch = !q || e.name?.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || e.status === statusFilter;
      const matchCat = categoryFilter === 'all' || e.category === categoryFilter;
      return matchSearch && matchStatus && matchCat;
    });
  }, [experiences, searchTerm, statusFilter, categoryFilter]);

  const stats = useMemo(() => ({
    total:   experiences.length,
    active:  experiences.filter(e => e.status === 'active').length,
    revenue: experiences.reduce((s, e) => s + (e.total_revenue || 0), 0),
    bookings:experiences.reduce((s, e) => s + (e.total_bookings || 0), 0),
  }), [experiences]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="experiences-page">
      <TopBar
        title="Experiencias"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/experience-widget')} className="h-8">
              <Settings className="h-3.5 w-3.5 mr-1" /> Widget
            </Button>
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white h-8"
              onClick={() => { setEditingExp(null); setShowForm(true); }}>
              <Plus className="h-4 w-4 mr-1" /> Nueva experiencia
            </Button>
          </div>
        }
      />

      {/* Stats bar */}
      <div className="bg-white border-b px-6 py-3">
        <div className="flex flex-wrap gap-6">
          {[
            { l: 'Total',     v: stats.total,                        icon: Sparkles,  c: 'text-slate-700' },
            { l: 'Activas',   v: stats.active,                       icon: PlayCircle,c: 'text-emerald-600' },
            { l: 'Reservas',  v: stats.bookings,                     icon: Calendar,  c: 'text-orange-600' },
            { l: 'Ingresos',  v: `$${stats.revenue.toLocaleString()}`,icon: TrendingUp,c: 'text-blue-600' },
          ].map(s => (
            <div key={s.l} className="flex items-center gap-2">
              <s.icon className={cn("h-4 w-4", s.c)} />
              <span className={cn("font-bold", s.c)}>{s.v}</span>
              <span className="text-xs text-slate-400">{s.l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b px-4 py-2 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input className="pl-8 h-8 text-sm" placeholder="Buscar experiencia..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 text-xs w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(EXP_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={fetchExperiences} className="p-1.5 rounded hover:bg-slate-100 ml-auto">
          <RefreshCw className="h-4 w-4 text-slate-400" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Sparkles className="h-14 w-14 text-slate-200 mx-auto mb-4" />
            <p className="font-medium text-slate-500">
              {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
                ? 'Sin resultados'
                : 'No hay experiencias creadas'}
            </p>
            {!searchTerm && statusFilter === 'all' && categoryFilter === 'all' && (
              <Button className="mt-4 bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => { setEditingExp(null); setShowForm(true); }}>
                <Plus className="h-4 w-4 mr-1" /> Crear primera experiencia
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map(exp => (
              <ExperienceCard
                key={exp.id}
                exp={exp}
                onEdit={e => { setEditingExp(e); setShowForm(true); }}
                onDelete={handleDelete}
                onStatusChange={handleStatusChange}
                onView={e => setViewingExp(e)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) { setShowForm(false); setEditingExp(null); }}}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-orange-500" />
              {editingExp ? 'Editar experiencia' : 'Nueva experiencia'}
            </DialogTitle>
          </DialogHeader>
          <ExperienceForm
            experience={editingExp}
            onSave={() => { setShowForm(false); setEditingExp(null); fetchExperiences(); }}
            onCancel={() => { setShowForm(false); setEditingExp(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Detail Drawer */}
      {viewingExp && (
        <ExperienceDetail exp={viewingExp} onClose={() => setViewingExp(null)} />
      )}
    </div>
  );
}
